// Table and Pagination: both work on markup a server already rendered.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

const ALIGN = ["left", "center", "right"];
const SORT_ICON = { asc: "arrow-up", desc: "arrow-down" };

/**
 * <i80-table density="medium" caption="Plans">
 *   <table>
 *     <thead><tr><th sort="asc">Name</th><th>Owner</th><th align="right">Tasks</th></tr></thead>
 *     <tbody><tr><td>Q3 launch</td><td>Ada</td><td align="right">14</td></tr></tbody>
 *   </table>
 * </i80-table>
 *
 * The <table> is the one you (or your server) wrote: this only adds the i80 classes and
 * the sort buttons. A <th sort> sorts the rows in the browser and fires i80-sort; with
 * sort-mode="server" it only fires the event, so the server can sort and swap the table.
 */
export class I80Table extends I80Element {
  static get observedAttributes() {
    return ["density", "valign", "striped", "layout", "caption", "sort-mode"];
  }

  setup() {
    this.adopt();
  }

  /** The table may be written after the tag (template engines, streamed HTML). */
  adopt() {
    const table = this.querySelector("table");
    if (!table || table === this._table) return;
    this._table = table;
    table.addEventListener("click", (e) => {
      const button = e.target.closest(".i80-table__th-button--sort");
      if (button) this._sort(button.closest("th"));
    });
  }

  update() {
    const table = this._table;
    if (!table) return;
    table.className = [
      "i80-table",
      this.flag("striped") && "i80-table--striped",
      this.getAttribute("layout") === "fixed" && "i80-table--layout-fixed",
      `i80-table--density-${this.choice("density", ["default", "short", "medium", "tall"], "medium")}`,
      `i80-table--valign-${this.choice("valign", ["baseline", "middle", "top"], "top")}`,
    ].filter(Boolean).join(" ");

    const caption = this.getAttribute("caption");
    if (caption) {
      const el = table.querySelector(":scope > caption") ?? table.insertBefore(h("caption"), table.firstChild);
      el.className = "sr-only";
      el.textContent = caption;
    }
    for (const head of table.tHead ? [table.tHead] : []) head.className = "i80-table__thead";
    for (const body of table.tBodies) body.className = "i80-table__tbody";
    for (const row of table.rows) row.className = ["i80-table__tr", row.hasAttribute("selected") && "i80-table__tr--is-selected"].filter(Boolean).join(" ");

    for (const cell of table.querySelectorAll("th")) this._head(cell);
    for (const cell of table.querySelectorAll("td")) {
      const align = ALIGN.includes(cell.getAttribute("align")) ? cell.getAttribute("align") : null;
      cell.className = ["i80-table__td", "i80-typography-body-200", "i80-font-weight-regular", align && `i80-table__td--align-${align}`].filter(Boolean).join(" ");
    }
  }

  _head(cell) {
    const align = ALIGN.includes(cell.getAttribute("align")) ? cell.getAttribute("align") : null;
    const sortable = cell.hasAttribute("sort");
    cell.className = ["i80-table__th", align && `i80-table__th--align-${align}`].filter(Boolean).join(" ");
    if (!cell.getAttribute("scope")) cell.setAttribute("scope", cell.closest("thead") ? "col" : "row");
    if (!sortable) {
      // The label is wrapped once; on later updates it is already there.
      if (!cell.querySelector(":scope > .i80-table__th-label")) {
        cell.replaceChildren(h("span", { class: "i80-table__th-label i80-typography-body-200 i80-font-weight-semibold" }, ...cell.childNodes));
      }
      cell.removeAttribute("aria-sort");
      return;
    }

    const order = ["asc", "desc"].includes(cell.getAttribute("sort")) ? cell.getAttribute("sort") : null;
    cell.setAttribute("aria-sort", order ? (order === "asc" ? "ascending" : "descending") : "none");
    let content = cell.querySelector(":scope > .i80-table__th-content");
    if (!content) {
      const label = h("span", { class: "i80-table__th-label i80-typography-body-200 i80-font-weight-semibold", id: nextId("th-label") }, ...cell.childNodes);
      content = h("div", { class: "i80-table__th-content" }, label,
        h("button", { type: "button", "data-i80-part": "sort", "aria-labelledby": `${nextId("th-sort")} ${label.id}` }));
      cell.replaceChildren(content);
    }
    const button = content.querySelector('[data-i80-part="sort"]');
    button.className = ["i80-table__th-button", "i80-table__th-button--sort", order && "i80-table__th-button--is-sorted"].filter(Boolean).join(" ");
    button.replaceChildren(
      h("span", { class: "i80-table__th-button-aria-label-hidden-segment", id: button.getAttribute("aria-labelledby").split(" ")[0] },
        order === "asc" ? "Sorted ascending by" : order === "desc" ? "Sorted descending by" : "Sort by"),
      icon(SORT_ICON[order] ?? "swap-vertical"),
    );
  }

  _sort(cell) {
    const order = cell.getAttribute("sort") === "asc" ? "desc" : "asc";
    for (const other of this._table.querySelectorAll("th[sort]")) {
      if (other !== cell) other.setAttribute("sort", "");
    }
    cell.setAttribute("sort", order);
    const key = cell.getAttribute("sort-key") ?? cell.querySelector(".i80-table__th-label")?.textContent.trim() ?? cell.textContent.trim();
    const index = [...cell.parentElement.children].indexOf(cell);

    if (this.getAttribute("sort-mode") !== "server") this.sortRows(index, order);
    this.update();
    this.dispatchEvent(new CustomEvent("i80-sort", { bubbles: true, detail: { key, order, index } }));
  }

  /** Sort the rows of every tbody by the values in one column. */
  sortRows(index, order) {
    const value = (row) => {
      const cell = row.children[index];
      return cell?.getAttribute("sort-value") ?? cell?.textContent.trim() ?? "";
    };
    const numeric = [...this._table.tBodies].every((body) =>
      [...body.rows].every((row) => value(row) === "" || !Number.isNaN(Number(value(row).replace(/[\s,]/g, "")))));
    const compare = numeric
      ? (a, b) => Number(value(a).replace(/[\s,]/g, "") || 0) - Number(value(b).replace(/[\s,]/g, "") || 0)
      : (a, b) => value(a).localeCompare(value(b), undefined, { numeric: true, sensitivity: "base" });
    for (const body of this._table.tBodies) {
      const rows = [...body.rows].sort((a, b) => (order === "asc" ? compare(a, b) : compare(b, a)));
      body.append(...rows);
    }
  }
}

/**
 * <i80-pagination total="248" page="3" per-page="25"></i80-pagination>
 * <i80-pagination type="compact" has-next></i80-pagination>
 *
 * Renders the Helios pagination as real links or buttons. With href-template
 * ("/plans?page={page}") they are links a crawler and a server can follow; without it
 * they are buttons that fire i80-page with the new page number.
 */
export class I80Pagination extends I80Element {
  static get observedAttributes() {
    return ["type", "total", "page", "per-page", "has-next", "has-previous", "href-template", "show-info", "show-size-selector", "page-sizes", "size-label", "aria-label"];
  }

  setup() {
    let root = this.part("pagination");
    if (!root) {
      root = h("div", { "data-i80-part": "pagination" });
      this.append(root);
    }
    this._root = root;
    root.addEventListener("click", (e) => {
      const target = e.target.closest("[data-i80-page]");
      if (target && target.localName === "button" && !target.disabled) this.goTo(Number(target.getAttribute("data-i80-page")));
    });
    root.addEventListener("change", (e) => {
      if (e.target.matches(".i80-pagination-size-selector select")) {
        this.setAttribute("per-page", e.target.value);
        this.dispatchEvent(new CustomEvent("i80-page-size", { bubbles: true, detail: { perPage: Number(e.target.value) } }));
        this.goTo(1);
      }
    });
  }

  get page() {
    return Math.max(1, parseInt(this.getAttribute("page") ?? "1", 10) || 1);
  }

  get perPage() {
    return Math.max(1, parseInt(this.getAttribute("per-page") ?? "25", 10) || 25);
  }

  /** Number of pages, or null when the caller doesn't know the total (compact). */
  get pages() {
    const total = parseInt(this.getAttribute("total") ?? "", 10);
    return Number.isNaN(total) ? null : Math.max(1, Math.ceil(total / this.perPage));
  }

  goTo(page) {
    const pages = this.pages;
    const next = pages ? Math.min(Math.max(1, page), pages) : Math.max(1, page);
    if (next === this.page) return;
    this.setAttribute("page", String(next));
    this.dispatchEvent(new CustomEvent("i80-page", { bubbles: true, detail: { page: next, perPage: this.perPage } }));
  }

  update() {
    const compact = this.choice("type", ["numbered", "compact"], "numbered") === "compact";
    const page = this.page;
    const pages = this.pages;
    this._root.className = `i80-pagination i80-pagination--${compact ? "compact" : "numbered"}`;
    const parts = [];

    const total = parseInt(this.getAttribute("total") ?? "", 10);
    if (this.flag("show-info") && !Number.isNaN(total)) {
      const from = total === 0 ? 0 : (page - 1) * this.perPage + 1;
      parts.push(h("div", { class: "i80-pagination-info i80-text i80-typography-body-100 i80-font-weight-medium" },
        `${from}–${Math.min(page * this.perPage, total)} of ${total}`));
    }

    const control = (extraClass, target, disabled, children, attrs = {}) => {
      const href = disabled ? null : this._href(target);
      const common = { class: `i80-pagination-nav__control ${extraClass}`, "data-i80-page": target, ...attrs };
      return href
        ? h("a", { ...common, href }, ...children)
        : h("button", { ...common, type: "button", disabled: disabled || null }, ...children);
    };
    const arrow = (dir, label, iconName, disabled) => {
      const text = h("span", { class: "i80-pagination-nav__arrow-label i80-text i80-typography-body-100 i80-font-weight-medium", "aria-hidden": "true" }, label);
      const children = dir === "prev" ? [icon(iconName), text] : [text, icon(iconName)];
      return control(`i80-pagination-nav__arrow i80-pagination-nav__arrow--direction-${dir}`, page + (dir === "next" ? 1 : -1), disabled, children, { "aria-label": label });
    };

    const hasPrev = this.hasAttribute("has-previous") ? this.flag("has-previous") : page > 1;
    const hasNext = this.hasAttribute("has-next") ? this.flag("has-next") : pages ? page < pages : true;
    const nav = h("nav", { class: "i80-pagination-nav", "aria-label": this._label() },
      arrow("prev", "Previous", "chevron-left", !hasPrev));

    if (!compact && pages) {
      const list = h("ul", { class: "i80-pagination-nav__page-list" });
      for (const n of pageNumbers(page, pages)) {
        list.append(h("li", { class: "i80-pagination-nav__page-item" },
          n === "…"
            ? h("div", { class: "i80-pagination-nav__ellipsis" }, "…")
            : control(`i80-pagination-nav__number${n === page ? " i80-pagination-nav__number--is-selected" : ""}`, n, false,
                [h("span", { class: "i80-text i80-typography-body-100 i80-font-weight-medium" }, h("span", { class: "sr-only" }, "page "), String(n))],
                { "aria-current": n === page ? "page" : null })));
      }
      nav.append(list);
    }
    nav.append(arrow("next", "Next", "chevron-right", !hasNext));
    parts.push(nav);

    if (this.flag("show-size-selector")) {
      const sizes = (this.getAttribute("page-sizes") ?? "10,25,50,100").split(",").map((s) => s.trim()).filter(Boolean);
      const id = nextId("page-size");
      parts.push(h("div", { class: "i80-pagination-size-selector" },
        h("label", { class: "i80-typography-body-100 i80-font-weight-medium", for: id }, this.getAttribute("size-label") ?? "Items per page"),
        h("select", { class: "i80-form-select i80-typography-body-200 i80-font-weight-regular", id },
          sizes.map((s) => h("option", { value: s, selected: Number(s) === this.perPage || null }, s)))));
    }

    this._root.replaceChildren(...parts);
  }

  /** Two paginations on one page (above and below a table) need different landmark names. */
  _label() {
    const own = this.getAttribute("aria-label");
    if (own) return own;
    const all = [...document.querySelectorAll("i80-pagination:not([aria-label])")];
    const index = all.indexOf(this);
    return index > 0 ? `Pagination ${index + 1}` : "Pagination";
  }

  _href(page) {
    const template = this.getAttribute("href-template");
    return template ? template.replace("{page}", String(page)).replace("{perPage}", String(this.perPage)) : null;
  }
}

/** 1 … 4 5 [6] 7 8 … 20 — the window Helios shows around the current page. */
function pageNumbers(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push("…");
  for (let n = from; n <= to; n += 1) out.push(n);
  if (to < pages - 1) out.push("…");
  out.push(pages);
  return out;
}

define("i80-table", I80Table);
define("i80-pagination", I80Pagination);
